import { Fragment } from 'react';
import { Button } from '../../../atoms/Button';
import { Typography } from '../../Typography';
import { CheckIcon } from 'morpheus-ui';
import { Icon } from '../../../atoms/Icon';

export type examItemAction = 'feedback' | 'start' | 'resume' | 'tryagain';

interface ExamItemProps {
  title: string;
  totalQuestions: number;
  status: 'done' | 'ongoing' | 'notstarted';
  correctAnswersPercentual?: number;
  lastAttemptDate?: string;
  isButtonLoading?: boolean;
  /** Estado de cada dia, no formato de dois dias. Ex.: "Dia 1: concluído · Dia 2: não iniciado". */
  daysLabel?: string;
  onButtonClick?: (action: examItemAction) => void;
  answers?: {
    correct: number;
    incorrect: number;
  };
}

const ExamItem = ({
  title,
  totalQuestions,
  status,
  correctAnswersPercentual = 0,
  lastAttemptDate,
  daysLabel,
  isButtonLoading = false,
  answers = {
    correct: 0,
    incorrect: 0,
  },
  onButtonClick = () => {},
}: ExamItemProps) => {
  const hasFinishedSimulated = status === 'done';
  const hasNotStartedSimulated = status === 'notstarted';

  const formattedCorrectAnswersPercentual = (correctAnswersPercentual || 0)
    .toFixed(1)
    .replace('.', ',');

  return (
    <li className='flex flex-col justify-between gap-md rounded-md bg-color-lightest p-4 shadow-level-1 md:flex-row'>
      <div className='xitems-start flex flex-col gap-lg'>
        <div className='flex w-fit flex-col gap-sm'>
          <div className='flex flex-col gap-sm md:flex-row'>
            {status === 'done' && (
              <div className='flex w-fit items-center gap-sm rounded-sm bg-feedback-color-success-pure px-2 '>
                <Typography size='small' className='text-[#fff]'>
                  Concluído
                </Typography>
              </div>
            )}
            {status === 'done' && lastAttemptDate && (
              <Typography size='small'>
                Última tentativa realizada em {lastAttemptDate}
              </Typography>
            )}
          </div>
          <Typography size='h4' type='title'>
            {title}
          </Typography>
          {daysLabel && (
            <Typography size='small' className='text-mono-color-dark'>
              {daysLabel}
            </Typography>
          )}
          <Typography size='small' className='flex flex-row gap-sm'>
            {totalQuestions} {totalQuestions > 1 ? 'questões' : 'questão'}{' '}
            {status === 'done' && (
              <span className='flex flex-row items-center gap-sm'>
                <Icon
                  Icon='check'
                  type='material'
                  className='text-md !text-feedback-color-success-pure'
                />
                {`${answers.correct} acertos`}
              </span>
            )}
            {status === 'done' && (
              <span className='flex flex-row items-center gap-sm'>
                <Icon
                  Icon='close'
                  type='material'
                  className='text-md !text-incidencia-n5'
                />
                {`${answers.incorrect} erros`}
              </span>
            )}
          </Typography>
        </div>
        <div className='hidden md:block'>
          <FooterButtons
            hasFinishedSimulated={hasFinishedSimulated}
            status={status}
            isButtonLoading={isButtonLoading}
            onButtonClick={onButtonClick}
          />
        </div>
      </div>

      {!hasNotStartedSimulated && (
        <div className='h-[2px] w-full bg-mono-color-black md:hidden' />
      )}

      <div className='flex !min-w-[120px] flex-row items-start justify-start gap-lg md:items-center md:justify-center'>
        {hasFinishedSimulated && (
          <>
            <div className='hidden h-full max-h-20 w-px bg-mono-color-black md:block' />
            <div className='flex w-full flex-col items-start justify-center gap-sm md:items-center '>
              <Typography className='!text-h6 text-mono-color-darkest'>
                Aproveitamento
              </Typography>
              <Typography className='!text-h2 font-bold text-mono-color-black'>
                {formattedCorrectAnswersPercentual}%
              </Typography>
            </div>
          </>
        )}
        {hasNotStartedSimulated && (
          <Button
            variant='contained'
            className='justify-center xs:w-full md:w-fit'
            onClick={() => onButtonClick('start')}
          >
            Fazer simulado
          </Button>
        )}
      </div>

      <div className='block md:hidden'>
        <FooterButtons
          hasFinishedSimulated={hasFinishedSimulated}
          status={status}
          isButtonLoading={isButtonLoading}
          onButtonClick={onButtonClick}
        />
      </div>
    </li>
  );
};

export default ExamItem;

const FooterButtons = ({
  hasFinishedSimulated,
  status,
  isButtonLoading,
  onButtonClick,
}: {
  hasFinishedSimulated: boolean;
  status: 'done' | 'ongoing' | 'notstarted';
  isButtonLoading: boolean;
  onButtonClick: (action: examItemAction) => void;
}) => {
  return (
    <div className='flex flex-col gap-lg xs:w-full md:w-fit md:flex-row'>
      {hasFinishedSimulated && (
        <Fragment>
          <Button
            className='md:justify-left order-2 justify-center px-0 xs:w-full md:order-1 md:w-fit'
            variant='text'
            loading={isButtonLoading}
            onClick={() => onButtonClick('tryagain')}
          >
            Refazer simulado
          </Button>
          <Button
            variant='contained'
            LeftIcon='checklist'
            iconType='material'
            className='order-1 justify-center xs:w-full md:order-2 md:w-fit'
            onClick={() => onButtonClick('feedback')}
          >
            Ver gabarito
          </Button>
        </Fragment>
      )}
      {status === 'ongoing' && (
        <Button
          variant='contained'
          className='justify-center xs:w-full md:w-fit'
          onClick={() => onButtonClick('resume')}
        >
          Continuar simulado
        </Button>
      )}
    </div>
  );
};
